import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import PhotoUpload from "../../components/PhotoUpload";
import { listDefaultSignatures, saveDefaultSignature, updateDefaultSignatureImage, deleteDefaultSignature } from "../../api/defaultSignatureApi";
import "../Attendance/Attendance.css";
import "../Users/UsersPage.css";

/**
 * Direct port of digicampusbd's `default-signature` ("Signature Setup") —
 * an institute's fixed signature slots (e.g. Principal, Headmaster) used on
 * printed documents — all of which remain Jasper-deferred elsewhere in this
 * migration; this is just the setup screen for the signature title + image
 * itself. New `DefaultSignature` entity/repository/service/controller,
 * reusing the already-built generic `FileStorageService`/`PhotoUpload`
 * component for the image half.
 */
export default function DefaultSignaturePage() {
  const [signatures, setSignatures] = useState([]);
  const [signatureTitle, setSignatureTitle] = useState("");
  const [usedId, setUsedId] = useState("1");
  const [usedName, setUsedName] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = () => listDefaultSignatures().then(setSignatures);

  useEffect(load, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!signatureTitle || !usedId) {
      setError("Please provide a title and a slot number.");
      return;
    }
    try {
      await saveDefaultSignature({ signatureTitle, usedId: Number(usedId), usedName, signStatus: 1 });
      setSuccess("Signature saved.");
      setSignatureTitle("");
      setUsedName("");
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save signature.");
    }
  };

  const handleUploadImage = (signatureId) => async (file) => {
    await updateDefaultSignatureImage(signatureId, file);
    load();
  };

  const handleDelete = async (signatureId) => {
    if (!window.confirm("Delete this signature?")) return;
    try {
      await deleteDefaultSignature(signatureId);
      load();
    } catch (err) {
      setError("Failed to delete signature.");
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Signature Setup</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      {success && <p style={{ color: "var(--color-success, green)" }}>{success}</p>}
      <form onSubmit={handleSave} className="attendance-filters">
        <label>
          Slot #
          <input type="number" value={usedId} onChange={(e) => setUsedId(e.target.value)} style={{ width: 60 }} />
        </label>
        <label>
          Title
          <input value={signatureTitle} onChange={(e) => setSignatureTitle(e.target.value)} placeholder="e.g. Principal" />
        </label>
        <label>
          Name
          <input value={usedName} onChange={(e) => setUsedName(e.target.value)} placeholder="Person's name" />
        </label>
        <button type="submit" className="btn-primary">
          Save
        </button>
      </form>

      <div className="table-scroll">
        <table className="attendance-table">
          <thead>
            <tr>
              <th>Slot</th>
              <th>Title</th>
              <th>Name</th>
              <th>Signature</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {signatures.map((s) => (
              <tr key={s.signatureId}>
                <td>{s.usedId}</td>
                <td>{s.signatureTitle}</td>
                <td>{s.usedName}</td>
                <td>
                  <PhotoUpload folder="signatures" fileName={s.signImageName} onUpload={handleUploadImage(s.signatureId)} />
                </td>
                <td>
                  <button type="button" className="btn-link danger" onClick={() => handleDelete(s.signatureId)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {signatures.length === 0 && (
              <tr>
                <td colSpan={5}>No signatures configured yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </MainLayout>
  );
}
